import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, User2, MessageSquare, Phone, Mic, Target, Award, AlertCircle, Loader2, Volume2, MessageSquareQuote, Save, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import VoiceTextInput from "../components/VoiceTextInput";
import SessionRecordingsPanel from "../components/SessionRecordingsPanel";

const ModeBadge = ({ mode }) => {
  const map = {
    text: { label: "Text-Chat", icon: MessageSquare },
    passive_call: { label: "Anruf · User Verkäufer", icon: Phone },
    active_call: { label: "Anruf · KI Verkäufer", icon: Phone },
  };
  const cfg = map[mode] || map.text;
  const Icon = cfg.icon;
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full bg-novatus-card-2 border border-novatus text-novatus-text-2">
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
};

const scoreColor = (s) => {
  if (s === null || s === undefined) return "text-novatus-text-3";
  if (s >= 70) return "text-novatus-green";
  if (s >= 50) return "text-amber-400";
  return "text-red-400";
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
  } catch (e) { return iso; }
};

const AdminSessionDetail = () => {
  const { sessionId } = useParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ttsLoading, setTtsLoading] = useState(null);
  const [audio, setAudio] = useState(null);
  const [coachingDraft, setCoachingDraft] = useState("");
  const [savingCoaching, setSavingCoaching] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/sessions/${sessionId}`);
        setDetail(data);
        setCoachingDraft(data?.coaching?.text || "");
      } catch (e) {
        toast.error("Session-Details konnten nicht geladen werden");
      } finally {
        setLoading(false);
      }
    })();
    return () => { if (audio) { try { audio.pause(); } catch (e) { /* noop */ } } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const saveCoaching = async () => {
    setSavingCoaching(true);
    try {
      const { data } = await api.post(`/admin/sessions/${sessionId}/coaching`, { text: coachingDraft });
      setDetail((prev) => prev ? { ...prev, coaching: data.coaching } : prev);
      toast.success(data.coaching ? "Coaching-Kommentar gespeichert" : "Coaching-Kommentar entfernt");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Speichern fehlgeschlagen";
      toast.error(detail);
    } finally {
      setSavingCoaching(false);
    }
  };

  const clearCoaching = async () => {
    if (!window.confirm("Coaching-Kommentar wirklich löschen? Der Verkäufer sieht ihn dann nicht mehr.")) return;
    setCoachingDraft("");
    setSavingCoaching(true);
    try {
      await api.post(`/admin/sessions/${sessionId}/coaching`, { text: "" });
      setDetail((prev) => prev ? { ...prev, coaching: null } : prev);
      toast.success("Kommentar entfernt");
    } catch (e) {
      toast.error("Entfernen fehlgeschlagen");
    } finally {
      setSavingCoaching(false);
    }
  };

  const playMessage = async (msg) => {
    if (audio) { try { audio.pause(); } catch (e) { /* noop */ } }
    setTtsLoading(msg.id);
    try {
      const { data } = await api.post("/tts", { text: msg.content });
      if (data?.audio_base64) {
        const a = new Audio("data:audio/mp3;base64," + data.audio_base64);
        a.onended = () => setTtsLoading(null);
        a.onerror = () => setTtsLoading(null);
        a.play();
        setAudio(a);
      } else {
        setTtsLoading(null);
      }
    } catch (e) {
      toast.error("Audio-Wiedergabe fehlgeschlagen");
      setTtsLoading(null);
    }
  };

  if (loading) return <div className="p-12 text-center text-novatus-text-3"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  if (!detail) return <div className="p-12 text-center text-novatus-text-3">Session nicht gefunden.</div>;

  const { session, scenario, user, messages, feedback, voice_summary } = detail;
  const fbData = feedback?.data || {};
  const isActiveCall = session?.mode === "active_call";

  // For active_call sessions: user plays customer, AI plays seller.
  // For all other modes: user plays seller, AI plays customer.
  const speakerLabel = (role) => {
    if (isActiveCall) {
      return role === "user" ? "Kunde (echter Mensch)" : "Verkäufer (KI-Demo)";
    }
    return role === "user" ? "Verkäufer (echter Mensch)" : "Kunde (KI)";
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <Link to="/team/sessions" className="text-sm text-novatus-text-3 hover:text-novatus-green inline-flex items-center gap-1 mb-4" data-testid="back-to-sessions-btn">
        <ArrowLeft className="w-4 h-4" /> Zurück zu allen Sessions
      </Link>

      {/* Header */}
      <div className="nv-card p-6 animate-slide-up">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="min-w-0">
            <span className="label-eyebrow">Session-Review</span>
            <h1 className="font-display font-black text-2xl sm:text-3xl text-white mt-1 leading-tight">{scenario?.title || session?.scenario_title}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <ModeBadge mode={session?.mode} />
              {scenario?.difficulty && (
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full diff-${scenario.difficulty}`}>{scenario.difficulty}</span>
              )}
              <span className="text-xs text-novatus-text-3">{formatDate(session?.ended_at || session?.created_at)}</span>
            </div>
          </div>

          <div className="flex items-center gap-6 shrink-0">
            <div className="text-right">
              <p className="label-eyebrow">Verkäufer</p>
              <p className="font-semibold text-white flex items-center gap-1.5 justify-end" data-testid="session-seller">
                <User2 className="w-4 h-4 text-novatus-text-3" />
                {user ? `${user.first_name} ${user.last_name}` : "—"}
              </p>
              <p className="text-xs text-novatus-text-3">{user?.email}</p>
            </div>
            <div className="text-center min-w-[80px]">
              <p className="label-eyebrow">Score</p>
              <p className={`font-display font-black text-4xl ${scoreColor(session?.score)}`} data-testid="session-score-large">{session?.score ?? "—"}</p>
              <p className="text-xs text-novatus-text-3">{session?.xp_earned ? `+${session.xp_earned} XP` : ""}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Feedback summary (if available) */}
      {feedback && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
          <div className="nv-card p-6 animate-slide-up delay-1" data-testid="feedback-strengths">
            <div className="flex items-center gap-2 mb-3">
              <Award className="w-5 h-5 text-novatus-green" />
              <h3 className="font-display font-bold text-white">Stärken</h3>
            </div>
            <ul className="space-y-2 text-sm text-novatus-text-2">
              {(fbData.strengths || []).length === 0 ? (
                <li className="text-novatus-text-3">— keine Stärken erfasst —</li>
              ) : (fbData.strengths || []).map((s, i) => (
                <li key={`str-${i}`} className="flex items-start gap-2">
                  <span className="text-novatus-green mt-1">●</span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="nv-card p-6 animate-slide-up delay-1" data-testid="feedback-improvements">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="w-5 h-5 text-amber-400" />
              <h3 className="font-display font-bold text-white">Verbesserungspotenzial</h3>
            </div>
            <ul className="space-y-2 text-sm text-novatus-text-2">
              {(fbData.improvements || []).length === 0 ? (
                <li className="text-novatus-text-3">— nichts erfasst —</li>
              ) : (fbData.improvements || []).map((s, i) => (
                <li key={`imp-${i}`} className="flex items-start gap-2">
                  <span className="text-amber-400 mt-1">●</span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Category scores */}
      {fbData.category_scores && Object.keys(fbData.category_scores).length > 0 && (
        <div className="nv-card p-6 mt-5 animate-slide-up delay-2" data-testid="feedback-categories">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-5 h-5 text-novatus-green" />
            <h3 className="font-display font-bold text-white">Kategorie-Scores</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {Object.entries(fbData.category_scores).map(([k, v]) => (
              <div key={k} className="bg-novatus-card-2 border border-novatus rounded-lg p-3 text-center">
                <div className={`font-display font-black text-2xl ${scoreColor(v)}`}>{v}</div>
                <div className="text-[10px] uppercase tracking-wider text-novatus-text-3 mt-1 capitalize leading-tight">{k.replace(/_/g, " ")}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Voice analytics summary */}
      {voice_summary && (
        <div className="nv-card p-6 mt-5 animate-slide-up delay-2" data-testid="voice-summary">
          <div className="flex items-center gap-2 mb-4">
            <Mic className="w-5 h-5 text-novatus-green" />
            <h3 className="font-display font-bold text-white">Voice-Analytics (aggregiert)</h3>
            <span className="ml-auto text-xs text-novatus-text-3">{voice_summary.voice_turns} Sprach-Turns · {voice_summary.total_speaking_s}s</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-novatus-card-2 border border-novatus rounded-lg p-3 text-center">
              <div className="font-display font-black text-2xl text-novatus-green">{voice_summary.avg_wpm}</div>
              <div className="text-[10px] uppercase tracking-wider text-novatus-text-3 mt-1">Ø WPM</div>
            </div>
            <div className="bg-novatus-card-2 border border-novatus rounded-lg p-3 text-center">
              <div className="font-display font-black text-2xl text-white capitalize">{voice_summary.pacing}</div>
              <div className="text-[10px] uppercase tracking-wider text-novatus-text-3 mt-1">Tempo</div>
            </div>
            <div className="bg-novatus-card-2 border border-novatus rounded-lg p-3 text-center">
              <div className="font-display font-black text-2xl text-white">{voice_summary.total_pauses}</div>
              <div className="text-[10px] uppercase tracking-wider text-novatus-text-3 mt-1">Pausen ({voice_summary.long_pauses} lang)</div>
            </div>
            <div className="bg-novatus-card-2 border border-novatus rounded-lg p-3 text-center">
              <div className={`font-display font-black text-2xl ${voice_summary.total_fillers > 5 ? "text-amber-400" : "text-white"}`}>{voice_summary.total_fillers}</div>
              <div className="text-[10px] uppercase tracking-wider text-novatus-text-3 mt-1">Füllwörter</div>
            </div>
          </div>
        </div>
      )}

      {/* Audio recordings — admin can listen back to each voice turn */}
      <div className="mt-5 animate-slide-up delay-2">
        <SessionRecordingsPanel sessionId={session?.id} eyebrow="Aufzeichnungen anhören" />
      </div>

      {/* Live-coaching hints emitted during the call (if any) */}
      {session?.coaching_hints?.length > 0 && (
        <div className="nv-card p-6 mt-5 animate-slide-up delay-2" data-testid="coaching-hints-card">
          <div className="flex items-center gap-2 mb-4">
            <MessageSquareQuote className="w-5 h-5 text-novatus-green" />
            <h3 className="font-display font-bold text-white">Live-Tipps im Anruf</h3>
            <span className="ml-auto text-xs text-novatus-text-3">{session.coaching_hints.length} Hinweise</span>
          </div>
          <div className="space-y-2">
            {session.coaching_hints.map((h, i) => (
              <div key={`adm-hint-${i}`} className={`flex items-start gap-3 p-3 rounded-lg border ${
                h.severity === "warn" ? "bg-amber-500/8 border-amber-400/30" : "bg-novatus-card-2 border-novatus"
              }`}>
                <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${
                  h.kind === "content" ? "bg-novatus-green/15 text-novatus-green" : "bg-novatus-card border-novatus text-novatus-text-2"
                }`}>{h.kind === "content" ? "Trainer" : "Voice"}</span>
                <p className="text-sm text-white flex-1">{h.text}</p>
                {h.created_at && (
                  <span className="text-[10px] text-novatus-text-3 shrink-0">
                    {new Date(h.created_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Coaching comment — Trainer's personal note for the seller */}
      <div className="nv-card p-6 mt-5 animate-slide-up delay-2" data-testid="coaching-card" style={{ borderColor: "rgba(31, 203, 79, 0.3)" }}>
        <div className="flex items-center gap-2 mb-3">
          <MessageSquareQuote className="w-5 h-5 text-novatus-green" />
          <h3 className="font-display font-bold text-white">Coaching-Kommentar</h3>
          {detail.coaching && (
            <span className="ml-auto text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-novatus-green/15 text-novatus-green border border-novatus-green/40">
              gespeichert
            </span>
          )}
        </div>
        <p className="text-xs text-novatus-text-3 mb-3 leading-relaxed">
          Dein persönlicher Hinweis an <span className="text-white font-semibold">{user?.first_name}</span>. Wird auf der Feedback-Seite des Verkäufers angezeigt. <span className="text-novatus-green">Tipp:</span> Tippe das Mikrofon für Spracheingabe.
        </p>
        <VoiceTextInput
          value={coachingDraft}
          onChange={setCoachingDraft}
          placeholder="z. B.: Bei Minute 3 zu schnell beim Preis – nimm dir mehr Zeit für die Wert-Argumentation."
          rows={4}
          testIdPrefix="coaching-input"
          disabled={savingCoaching}
          maxLength={4000}
        />
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <button
            onClick={saveCoaching}
            disabled={savingCoaching || !coachingDraft.trim() || coachingDraft.trim() === (detail.coaching?.text || "")}
            className="btn-green h-10 px-4 rounded-lg flex items-center gap-2 disabled:opacity-50"
            data-testid="coaching-save-btn"
          >
            {savingCoaching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Speichern
          </button>
          {detail.coaching && (
            <button
              onClick={clearCoaching}
              disabled={savingCoaching}
              className="h-10 px-3 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 flex items-center gap-2 text-sm"
              data-testid="coaching-clear-btn"
            >
              <Trash2 className="w-3.5 h-3.5" /> Entfernen
            </button>
          )}
          {detail.coaching && (
            <span className="text-xs text-novatus-text-3 ml-auto">
              von <span className="text-white">{detail.coaching.by_name}</span> · {formatDate(detail.coaching.updated_at)}
            </span>
          )}
        </div>
      </div>

      {/* Transcript */}
      <div className="nv-card mt-5 animate-slide-up delay-3" data-testid="transcript-card">
        <div className="p-4 border-b border-novatus flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-novatus-green" />
          <h3 className="font-display font-bold text-white">Transkript ({messages.length} Nachrichten)</h3>
          <p className="ml-auto text-xs text-novatus-text-3">Klick auf Wiedergabe spricht die KI-Stimme nach.</p>
        </div>
        <div className="divide-y divide-novatus max-h-[600px] overflow-y-auto">
          {messages.length === 0 ? (
            <p className="p-6 text-sm text-novatus-text-3">Keine Nachrichten in dieser Session.</p>
          ) : messages.map((m, i) => {
            const isUser = m.role === "user";
            return (
              <div key={m.id || i} className={`p-4 ${isUser ? "bg-novatus-card-2/40" : ""}`} data-testid={`transcript-message-${i}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                    isUser ? "bg-novatus-card-2 border border-novatus" : "bg-novatus-green/15 border border-novatus-green/40"
                  }`}>
                    {isUser ? <User2 className="w-3.5 h-3.5 text-novatus-text-2" /> : <MessageSquare className="w-3.5 h-3.5 text-novatus-green" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className={`text-xs font-semibold ${isUser ? "text-white" : "text-novatus-green"}`}>{speakerLabel(m.role)}</p>
                      {m.modality === "voice" && (
                        <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-novatus-green/10 text-novatus-green border border-novatus-green/30">Voice</span>
                      )}
                      <span className="text-[10px] text-novatus-text-3">{formatDate(m.created_at)}</span>
                      <button
                        onClick={() => playMessage(m)}
                        disabled={ttsLoading === m.id}
                        className="ml-auto text-xs text-novatus-text-3 hover:text-novatus-green inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`play-message-${i}`}
                        title="Mit KI-Stimme vorlesen"
                      >
                        {ttsLoading === m.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Volume2 className="w-3 h-3" />}
                        Wiedergabe
                      </button>
                    </div>
                    <p className="text-sm text-white whitespace-pre-wrap leading-relaxed">{m.content}</p>
                    {m.voice_stats && (
                      <div className="mt-2 flex flex-wrap gap-2 text-[10px] uppercase tracking-wider text-novatus-text-3">
                        <span><span className="text-novatus-green font-bold">{m.voice_stats.wpm}</span> WPM</span>
                        <span>·</span>
                        <span><span className="text-white capitalize">{m.voice_stats.pacing}</span></span>
                        <span>·</span>
                        <span><span className="text-white">{m.voice_stats.pause_count}</span> Pausen</span>
                        {m.voice_stats.filler_count > 0 && (
                          <>
                            <span>·</span>
                            <span className={m.voice_stats.filler_count > 3 ? "text-amber-400" : ""}>
                              <span className="font-bold">{m.voice_stats.filler_count}</span> Füller
                            </span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AdminSessionDetail;
